import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getQueryKey as companyNotesGetQueryKey } from '@/components/Note/hooks/useCompanyNotes';
import { getQueryKey as noteGetQueryKey } from '@/components/Note/hooks/useNote';

import type { NoteReminderQueryClientData } from './types';
import { mutationFn } from './mutationFn';
import { getQueryKey as noteRemindersGetQueryKey } from '../utils';

export interface UseCreateNoteReminderParameters {
  companyId: string;
  noteId: string;
}

/**
 * This hook can be used to create a note reminder
 * @param {string} companyId required to invalidate CompanyNotes cache
 * @param {string} noteId required to invalidate NoteReminder cache
 *
 * @returns {Object}
 *  - `mutate` a function which can be called to create a new note reminder
 *  - `status` a flag to indicate state of the request
 */
export const useCreateNoteReminder = ({
  companyId,
  noteId,
}: UseCreateNoteReminderParameters) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      const NOTE_REMINDERS_QUERY_KEY = noteRemindersGetQueryKey(noteId);
      queryClient.setQueryData<NoteReminderQueryClientData>(
        NOTE_REMINDERS_QUERY_KEY,
        data
      );
    },
    onSettled: () => {
      const COMPANY_NOTES_QUERY_KEY = companyNotesGetQueryKey(companyId);
      const NOTE_REMINDERS_QUERY_KEY = noteRemindersGetQueryKey(noteId);
      const NOTE_QUERY_KEY = noteGetQueryKey(noteId);

      queryClient.invalidateQueries(COMPANY_NOTES_QUERY_KEY);
      queryClient.invalidateQueries(NOTE_REMINDERS_QUERY_KEY);
      queryClient.invalidateQueries(NOTE_QUERY_KEY);
    },
  });
};
